<template>
    <div style="margin:20px 0px;">
        <!-- Кнопка "Запит на доступ" -->
        <div class="mb-3">
            <button
                @click="showRequestModal = true"
                class="btn btn-primary"
            >
                <i class="bx bx-plus"></i> {{ $t('RequestAccess') }}
            </button>
        </div>

        <!-- Перемикач між активними та пасивними доступами -->
        <div class="mb-3">
            <div class="btn-group" role="group">
                <button
                    type="button"
                    :class="`btn btn-${activeTab === 'active' ? 'primary' : 'outline-primary'}`"
                    @click="activeTab = 'active'"
                >
                    {{ $t('ActiveAccesses') }}
                    <span v-if="activeAccesses.length > 0" class="badge bg-light text-dark ms-1">
                        {{ activeAccesses.length }}
                    </span>
                </button>
                <button
                    type="button"
                    :class="`btn btn-${activeTab === 'inactive' ? 'primary' : 'outline-primary'}`"
                    @click="activeTab = 'inactive'"
                >
                    {{ $t('InactiveAccesses') }}
                    <span v-if="inactiveAccesses.length > 0" class="badge bg-light text-dark ms-1">
                        {{ inactiveAccesses.length }}
                    </span>
                </button>
            </div>
        </div>

        <!-- Лоадер завантаження -->
        <div v-if="loading" class="text-center py-4">
            <b-spinner variant="primary"></b-spinner>
            <p class="mt-2">{{ $t('Loading') }}...</p>
        </div>

        <!-- Активні доступи -->
        <div v-else-if="activeTab === 'active'">
            <div v-if="activeAccesses.length > 0">
                <div class="table-responsive">
                    <table class="table table-striped table-hover">
                        <thead>
                            <tr>
                                <th>{{ $t('ResourceName') }}</th>
                                <th>{{ $t('AccessType') }}</th>
                                <th>{{ $t('GrantedDate') }}</th>
                                <th>{{ $t('Status') }}</th>
                                <th>{{ $t('Actions') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="access in activeAccesses" :key="access.id">
                                <td>{{ access.resourceName }}</td>
                                <td>
                                    <span class="badge bg-info">{{ access.accessType }}</span>
                                </td>
                                <td>{{ formatDate(access.grantedDate) }}</td>
                                <td>
                                    <span class="badge bg-success">{{ $t('Active') }}</span>
                                </td>
                                <td>
                                    <button
                                        @click="goToResource(access)"
                                        class="btn btn-sm btn-primary"
                                    >
                                        <i class="bx bx-link-external"></i> {{ $t('GoTo') }}
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div v-else class="alert alert-info">
                <i class="bx bx-info-circle me-2"></i>
                {{ $t('NoActiveAccesses') }}
            </div>
        </div>

        <!-- Пасивні доступи -->
        <div v-else-if="activeTab === 'inactive'">
            <div v-if="inactiveAccesses.length > 0">
                <div class="table-responsive">
                    <table class="table table-striped table-hover">
                        <thead>
                            <tr>
                                <th>{{ $t('ResourceName') }}</th>
                                <th>{{ $t('AccessType') }}</th>
                                <th>{{ $t('GrantedDate') }}</th>
                                <th>{{ $t('RevokedDate') }}</th>
                                <th>{{ $t('Status') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="access in inactiveAccesses" :key="access.id">
                                <td>{{ access.resourceName }}</td>
                                <td>
                                    <span class="badge bg-secondary">{{ access.accessType }}</span>
                                </td>
                                <td>{{ formatDate(access.grantedDate) }}</td>
                                <td>{{ formatDate(access.revokedDate) }}</td>
                                <td>
                                    <span class="badge bg-danger">{{ $t('Inactive') }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div v-else class="alert alert-info">
                <i class="bx bx-info-circle me-2"></i>
                {{ $t('NoInactiveAccesses') }}
            </div>
        </div>

        <!-- Модальне вікно запиту на доступ -->
        <modal
            v-if="showRequestModal"
            @close="closeRequestModal"
            :title="$t('RequestAccessToAccount')"
        >
            <template v-slot:body>
                <!-- Форма запиту на доступ -->
                <div v-if="!accessRequestSent">
                    <!-- Інформаційне повідомлення -->
                    <div class="alert alert-info" role="alert">
                        <i class="bx bx-info-circle me-2"></i>
                        {{ $t('AccessRequestInfoMessage') }}
                    </div>

                    <div class="mb-3">
                        <label class="form-label">
                            {{ $t('AccessScope') }} <span class="text-danger">*</span>
                        </label>
                        <select
                            v-model="accessRequest.scope"
                            class="form-select"
                        >
                            <option value="">{{ $t('SelectScope') }}</option>
                            <option value="pos">{{ $t('POSTerminal') }}</option>
                            <option value="admin">{{ $t('AdminPanel') }}</option>
                            <option value="analytics">{{ $t('Analytics') }}</option>
                            <option value="warehouse">{{ $t('Warehouse') }}</option>
                        </select>
                    </div>

                    <div class="mb-3">
                        <label class="form-label">
                            {{ $t('Reason') }} <span class="text-danger">*</span>
                        </label>
                        <textarea
                            v-model="accessRequest.reason"
                            class="form-control"
                            rows="3"
                            :placeholder="$t('EnterReasonForAccess')"
                        ></textarea>
                        <div class="form-text">
                            {{ $t('ReasonExample') }}
                        </div>
                    </div>
                </div>

                <!-- Лоадер очікування відповіді -->
                <div v-else-if="waitingForResponse" class="text-center py-4">
                    <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
                    <h5 class="mt-3">{{ $t('WaitingForApproval') }}</h5>
                    <p class="text-muted">{{ $t('YourRequestIsBeingReviewed') }}</p>
                    <div class="mt-4">
                        <div class="progress" style="height: 25px;">
                            <div
                                class="progress-bar progress-bar-striped progress-bar-animated"
                                role="progressbar"
                                :style="`width: ${requestProgress}%`"
                            >
                                {{ requestProgress }}%
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Повідомлення про успіх -->
                <div v-else-if="requestApproved" class="text-center py-4">
                    <div class="mb-4">
                        <i class="bx bx-check-circle text-success" style="font-size: 4rem;"></i>
                    </div>
                    <h5 class="text-success mb-3">{{ $t('AccessGranted') }}</h5>
                    <p class="text-muted">{{ $t('YourAccessRequestHasBeenApproved') }}</p>
                </div>
            </template>

            <template v-slot:footer-bottom>
                <!-- Кнопки форми -->
                <div v-if="!accessRequestSent" class="d-flex gap-2 w-100">
                    <button
                        @click="closeRequestModal"
                        class="btn btn-secondary"
                    >
                        {{ $t('Cancel') }}
                    </button>
                    <button
                        @click="submitAccessRequest"
                        class="btn btn-primary"
                        :disabled="!isFormValid || sendingRequest"
                    >
                        <span v-if="!sendingRequest">
                            <i class="bx bx-send"></i> {{ $t('SendRequest') }}
                        </span>
                        <span v-else>
                            <b-spinner small></b-spinner> {{ $t('Sending') }}...
                        </span>
                    </button>
                </div>

                <!-- Кнопка скасування під час очікування -->
                <button
                    v-else-if="waitingForResponse"
                    @click="cancelRequest"
                    class="btn btn-outline-danger"
                >
                    {{ $t('CancelRequest') }}
                </button>

                <!-- Кнопки після схвалення -->
                <div v-else-if="requestApproved" class="d-flex gap-2 w-100">
                    <button
                        @click="goToGrantedResource"
                        class="btn btn-primary"
                    >
                        <i class="bx bx-link-external"></i> {{ $t('GoToResource') }}
                    </button>
                    <button
                        @click="closeAndReset"
                        class="btn btn-outline-primary"
                    >
                        {{ $t('Close') }}
                    </button>
                </div>
            </template>
        </modal>
    </div>
</template>

<script>
import { storeS } from '@/store';
import modal from '@/components/modal/modal-lg';

export default {
    props: ['number'],
    components: {
        modal
    },
    data() {
        return {
            loading: false,
            sendingRequest: false,
            waitingForResponse: false,
            accessRequestSent: false,
            requestApproved: false,
            requestProgress: 0,
            showRequestModal: false,
            activeTab: 'active',
            activeAccesses: [],
            inactiveAccesses: [],
            grantedResourceUrl: '',
            accessRequest: {
                scope: '',
                reason: ''
            }
        }
    },
    created() {
        this.loadAccessData();
    },
    computed: {
        perms() {
            return storeS.perms;
        },
        isFormValid() {
            return this.accessRequest.scope &&
                   this.accessRequest.reason &&
                   this.accessRequest.reason.length >= 10;
        }
    },
    methods: {
        loadAccessData() {
            this.loading = true;
            // TODO: Замінити на реальний API-запит
            // Симуляція завантаження даних
            setTimeout(() => {
                // Приклад даних для активних доступів
                this.activeAccesses = [
                    {
                        id: 1,
                        resourceName: 'CRM Dashboard',
                        accessType: 'Read/Write',
                        grantedDate: '2025-01-15',
                        url: '/crm/dashboard'
                    },
                    {
                        id: 2,
                        resourceName: 'Analytics Module',
                        accessType: 'Read Only',
                        grantedDate: '2025-02-01',
                        url: '/analytics'
                    }
                ];

                // Приклад даних для пасивних доступів
                this.inactiveAccesses = [
                    {
                        id: 3,
                        resourceName: 'Old Reporting System',
                        accessType: 'Admin',
                        grantedDate: '2024-05-10',
                        revokedDate: '2025-01-01'
                    }
                ];

                this.loading = false;
            }, 1000);

            // Реальний API-запит має виглядати так:
            // apiServe.getClientAccesses(this.number).then(res => {
            //     if(res.status == 'done') {
            //         this.activeAccesses = res.data.active || [];
            //         this.inactiveAccesses = res.data.inactive || [];
            //     }
            //     this.loading = false;
            // }).catch(err => {
            //     this.$toast.error(this.$t('error'));
            //     this.loading = false;
            // });
        },
        formatDate(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return date.toLocaleDateString('uk-UA');
        },
        goToResource(access) {
            if (access.url) {
                this.$router.push(access.url);
            } else {
                this.$toast.info(this.$t('ResourceUrlNotAvailable'));
            }
        },
        submitAccessRequest() {
            if (!this.isFormValid) {
                this.$toast.warning(this.$t('FillRequiredFields'));
                return;
            }

            this.sendingRequest = true;

            // TODO: Замінити на реальний API-запит
            setTimeout(() => {
                this.sendingRequest = false;
                this.accessRequestSent = true;
                this.waitingForResponse = true;
                this.startProgressSimulation();
                this.$toast.success(this.$t('RequestSent'));

                // Симуляція отримання відповіді
                setTimeout(() => {
                    this.waitingForResponse = false;
                    this.requestApproved = true;
                    this.grantedResourceUrl = '/granted-resource';
                }, 5000);
            }, 1500);

            // Реальний API-запит має виглядати так:
            // apiServe.submitAccessRequest({
            //     clientPhone: this.number,
            //     ...this.accessRequest
            // }).then(res => {
            //     if(res.status == 'done') {
            //         this.sendingRequest = false;
            //         this.accessRequestSent = true;
            //         this.waitingForResponse = true;
            //         this.pollRequestStatus(res.data.requestId);
            //         this.$toast.success(this.$t('RequestSent'));
            //     }
            // }).catch(err => {
            //     this.sendingRequest = false;
            //     this.$toast.error(this.$t('error'));
            // });
        },
        startProgressSimulation() {
            this.requestProgress = 0;
            const interval = setInterval(() => {
                if (this.requestProgress < 90) {
                    this.requestProgress += 10;
                } else {
                    clearInterval(interval);
                }
            }, 500);
        },
        cancelRequest() {
            this.waitingForResponse = false;
            this.accessRequestSent = false;
            this.requestProgress = 0;
            this.$toast.info(this.$t('RequestCancelled'));
        },
        goToGrantedResource() {
            if (this.grantedResourceUrl) {
                this.$router.push(this.grantedResourceUrl);
            }
            this.closeAndReset();
        },
        closeRequestModal() {
            this.showRequestModal = false;
        },
        closeAndReset() {
            this.resetForm();
            this.accessRequestSent = false;
            this.waitingForResponse = false;
            this.requestApproved = false;
            this.requestProgress = 0;
            this.showRequestModal = false;
            this.loadAccessData(); // Оновлюємо список доступів
        },
        resetForm() {
            this.accessRequest = {
                scope: '',
                reason: ''
            };
        }
    }
}
</script>

<style scoped>
.table {
    margin-bottom: 0;
}

.progress {
    margin-top: 1rem;
}

.btn-group {
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
</style>
